import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    BookOpen, PlusCircle, X, CheckCircle2, AlertTriangle, Trash2, Edit3, ShieldCheck, Save, Plus 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { getUserPermissions } from '../../utils/permissions_utils';
import API, { socket } from '../../services/api';

const Matieres = ({ user }) => {
    const navigate = useNavigate();
    const userPerms = useMemo(() => getUserPermissions(), []);
    
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmAction, setConfirmAction] = useState({ show: false, id: null, type: null, message: '' });
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [matieres, setMatieres] = useState([]);
    
    const [matiereForm, setMatiereForm] = useState({
        code_matiere: '',
        libelle: '',
        description: ''
    });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get('/scolarite/matieres/liste');
            if (res.data.success) setMatieres(res.data.data);
        } catch (err) {
            showToast("Erreur lors du chargement du catalogue des matières", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        if (socket && user?.company_id) {
            const room = String(user.company_id);
            socket.emit('join_company', room);
            socket.on('DATA_EVENT', (event) => {
                if (['matieres'].includes(event.table)) {
                    fetchData();
                }
            });
        }
        return () => {
            if (socket) socket.off('DATA_EVENT');
        };
    }, [fetchData, user?.company_id]);

    const resetForms = () => {
        setShowForm(false);
        setEditingId(null);
        setMatiereForm({ code_matiere: '', libelle: '', description: '' });
    };

    const handleMatiereSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (editingId) {
                await API.put(`/scolarite/matieres/${editingId}`, matiereForm);
                showToast("Matière mise à jour avec succès.");
            } else {
                await API.post('/scolarite/matieres/creer', matiereForm);
                showToast("Matière créée avec succès.");
            }
            resetForms();
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Une erreur est survenue", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditMatiere = (mat) => {
        setEditingId(mat.id);
        setMatiereForm({
            code_matiere: mat.code_matiere,
            libelle: mat.libelle,
            description: mat.description || ''
        });
        setShowForm(true);
    };

    const askDelete = (id, label) => {
        setConfirmAction({
            show: true,
            id,
            message: `Confirmer la suppression de la matière ${label} ?`
        });
    };

    const processConfirm = async () => {
        const { id } = confirmAction;
        setConfirmAction({ show: false });
        setActionLoading(true);
        try {
            await API.delete(`/scolarite/matieres/${id}`);
            showToast("Matière supprimée avec succès.");
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Suppression refusée", "error");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <header style={headerStyle}>
                    <div style={titleContainer}>
                        <div style={iconBoxStyle}><BookOpen size={22} color="white" /></div>
                        <div>
                            <h1 style={titleStyle}>CATALOGUE GLOBAL DES MATIÈRES</h1>
                            <div style={subtitleStyle}><ShieldCheck size={14} /> Référentiel des matières d'enseignement</div>
                        </div>
                    </div>
                    <button 
                        onClick={() => {
                            if (showForm) resetForms();
                            else { resetForms(); setShowForm(true); }
                        }} 
                        style={showForm ? btnCancel : btnAdd}
                    >
                        {showForm ? <><X size={18} /> ANNULER</> : <><PlusCircle size={18} /> NOUVELLE MATIÈRE</>}
                    </button>
                </header>

                {showForm && (
                    <div style={formWrapper}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                            <span style={{fontSize: '12px', fontWeight: '900', color: '#2563eb', textTransform: 'uppercase'}}>
                                {editingId ? "Modifier la matière" : "Enregistrer une nouvelle matière"}
                            </span>
                            <button onClick={resetForms} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'}}>
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleMatiereSubmit} style={inlineForm}>
                            <div style={{...inputGroup, maxWidth: '160px'}}>
                                <label style={labelStyle}>CODE MATIÈRE *</label>
                                <input 
                                    style={inputStyle} required placeholder="Ex: MATH"
                                    value={matiereForm.code_matiere} 
                                    onChange={(e) => setMatiereForm({...matiereForm, code_matiere: e.target.value})} 
                                />
                            </div>
                            <div style={inputGroup}>
                                <label style={labelStyle}>LIBELLÉ DE LA MATIÈRE *</label>
                                <input 
                                    style={inputStyle} required placeholder="Ex: Mathématiques"
                                    value={matiereForm.libelle} 
                                    onChange={(e) => setMatiereForm({...matiereForm, libelle: e.target.value})} 
                                />
                            </div>
                            <div style={inputGroup}>
                                <label style={labelStyle}>DESCRIPTION (OPTIONNEL)</label>
                                <input 
                                    style={inputStyle} placeholder="Précisions..."
                                    value={matiereForm.description} 
                                    onChange={(e) => setMatiereForm({...matiereForm, description: e.target.value})} 
                                />
                            </div>
                            <button type="submit" disabled={actionLoading} style={btnSubmit}>
                                {editingId ? "METTRE À JOUR" : "ENREGISTRER"}
                            </button>
                        </form>
                    </div>
                )}

                <div style={tableWrapper}>
                    {loading ? (
                        <div style={centerStyle}><BookOpen className="animate-spin" size={40} color="#2563eb" /></div>
                    ) : (
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>CODE</th>
                                    <th style={thStyle}>LIBELLÉ</th>
                                    <th style={thStyle}>DESCRIPTION</th>
                                    <th style={{...thStyle, textAlign: 'right'}}>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matieres.map(mat => (
                                    <tr key={mat.id} style={trStyle}>
                                        <td style={tdStyle}><code style={codeBadge}>{mat.code_matiere}</code></td>
                                        <td style={tdStyle}><strong>{mat.libelle}</strong></td>
                                        <td style={tdStyle}>{mat.description || <span style={{color: '#94a3b8'}}>Aucune description</span>}</td>
                                        <td style={{...tdStyle, textAlign: 'right'}}>
                                            <div style={{display:'flex', gap:'8px', justifyContent: 'flex-end'}}>
                                                <button onClick={() => handleEditMatiere(mat)} style={btnEdit} title="Modifier"><Edit3 size={16} /></button>
                                                <button onClick={() => askDelete(mat.id, mat.libelle)} style={btnDelete} title="Supprimer"><Trash2 size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {matieres.length === 0 && (
                                    <tr><td colSpan="4" style={emptyStyle}>Aucune matière enregistrée dans le catalogue.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {confirmAction.show && (
                    <div style={confirmToastStyle}>
                        <div style={{display:'flex', alignItems:'center', gap:'12px', flex: 1}}>
                            <div style={warnIcon}><AlertTriangle size={20} color="white"/></div>
                            <span style={confirmText}>{confirmAction.message}</span>
                        </div>
                        <div style={{display:'flex', gap:'10px'}}>
                            <button onClick={()=>setConfirmAction({show:false})} style={btnToastCancel}>ANNULER</button>
                            <button onClick={processConfirm} style={btnToastConfirm}>CONFIRMER</button>
                        </div>
                    </div>
                )}

                {toast.show && (
                    <div style={{...toastStyle, background: toast.type === 'success' ? '#0f172a' : '#dc2626'}}>
                        {toast.type === 'success' ? <CheckCircle2 size={18} color="#10b981" /> : <AlertTriangle size={18} color="white" />}
                        {toast.message}
                    </div>
                )}
            </main>
        </div>
    );
};

// --- STYLES CSS-IN-JS ---
const layoutStyle = { display: 'flex', height: '100vh', background: '#f8fafc', overflow: 'hidden' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
const headerStyle = { background: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const titleContainer = { display: 'flex', alignItems: 'center', gap: '15px' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#0f172a' };
const subtitleStyle = { fontSize: '12px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' };
const iconBoxStyle = { background: '#2563eb', padding: '10px', borderRadius: '12px' };
const tableWrapper = { padding: '20px 40px', flex: 1, overflowY: 'auto' };
const tableStyle = { width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px' };
const thStyle = { textAlign: 'left', padding: '10px', fontSize: '11px', color: '#64748b', fontWeight: '800', textTransform: 'uppercase' };
const trStyle = { background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' };
const tdStyle = { padding: '15px 10px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', fontSize: '13px' };
const codeBadge = { background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '800' };
const emptyStyle = { textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: '600' };
const btnEdit = { background: '#eff6ff', border: 'none', color: '#2563eb', padding: '6px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const btnDelete = { background: '#fef2f2', border: 'none', color: '#dc2626', padding: '6px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const formWrapper = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.01)' };
const inlineForm = { display: 'flex', alignItems: 'flex-end', gap: '20px', flexWrap: 'wrap' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '180px' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#64748b' };
const inputStyle = { padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', fontSize: '12px', width: '100%', background: 'white' };
const btnSubmit = { background: '#059669', color: 'white', border: 'none', padding: '9px 20px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' };
const btnAdd = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const btnCancel = { ...btnAdd, background: '#fee2e2', color: '#dc2626' };
const confirmToastStyle = { position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '15px 25px', borderRadius: '20px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', minWidth: '500px', zIndex: 9999, border: '2px solid #e2e8f0' };
const warnIcon = { background: '#f59e0b', padding: '8px', borderRadius: '12px' };
const confirmText = { fontWeight: '800', color: '#0f172a', fontSize: '13px' };
const btnToastConfirm = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const btnToastCancel = { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const toastStyle = { position: 'fixed', top: '20px', right: '20px', padding: '15px 30px', borderRadius: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 9999, fontWeight: '700' };
const centerStyle = { display: 'flex', justifyContent: 'center', padding: '100px' };

export default Matieres;